import moment from "moment-timezone";

// Everything the calendar derives from an event's startTime + durationMinutes
// lives here: display formatting, the next-occurrence date math, the .ics
// download, and the Google Calendar deep-link.
//
// Timezone rules (both matter, independently):
// 1. Never pin the event to a UTC instant. 4:30pm Eastern is 20:30Z in summer
//    and 21:30Z in winter, so times are emitted as naive wall-clock values
//    with TZID/ctz carrying the zone. No trailing "Z" on DTSTART/DTEND/dates.
// 2. Compute the occurrence date on the Eastern calendar, not the browser's —
//    a viewer in another timezone can be on a different weekday than New York.

const EASTERN_TZ = "America/New_York";

const BYDAY = { 1: "MO", 2: "TU", 3: "WE", 4: "TH", 5: "FR" };

// Naive local timestamp (no zone suffix) used by both the .ics fields and the
// Google Calendar "dates" param.
const ICS_LOCAL_FORMAT = "YYYYMMDD[T]HHmmss";

// Hardcoded VTIMEZONE for America/New_York (current US DST rules). Google and
// Apple resolve a bare IANA TZID fine, but Outlook desktop is stricter and
// wants the definition embedded.
const NEW_YORK_VTIMEZONE = [
  "BEGIN:VTIMEZONE",
  `TZID:${EASTERN_TZ}`,
  "BEGIN:DAYLIGHT",
  "TZOFFSETFROM:-0500",
  "TZOFFSETTO:-0400",
  "TZNAME:EDT",
  "DTSTART:19700308T020000",
  "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU",
  "END:DAYLIGHT",
  "BEGIN:STANDARD",
  "TZOFFSETFROM:-0400",
  "TZOFFSETTO:-0500",
  "TZNAME:EST",
  "DTSTART:19701101T020000",
  "RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU",
  "END:STANDARD",
  "END:VTIMEZONE",
];

// "14:30" -> "2:30pm", "09:00" -> "9am" (round hours drop the ":00").
export function formatTimeShort(hhmm) {
  const [hourStr, minuteStr] = hhmm.split(":");
  const hour24 = Number(hourStr);
  const suffix = hour24 >= 12 ? "pm" : "am";
  const hour12 = hour24 % 12 || 12;
  return minuteStr === "00"
    ? `${hour12}${suffix}`
    : `${hour12}:${minuteStr}${suffix}`;
}

function addMinutesToTime(hhmm, minutes) {
  const [hours, mins] = hhmm.split(":").map(Number);
  const total = hours * 60 + mins + minutes;
  const endHours = Math.floor(total / 60) % 24;
  const endMinutes = total % 60;
  return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
}

// "09:00" + 90 -> "9am – 10:30am" (en dash, as in the design).
export function formatTimeRange(startTime, durationMinutes) {
  const endTime = addMinutesToTime(startTime, durationMinutes);
  return `${formatTimeShort(startTime)} – ${formatTimeShort(endTime)}`;
}

// Next occurrence of an ISO weekday (1=Mon..5=Fri) on the Eastern calendar,
// rolling a week forward when that day is already past — or is today but the
// event's start time has passed. Returns "YYYY-MM-DD".
export function nextOccurrenceET(isoDay, startTime) {
  const nowEt = moment.tz(EASTERN_TZ);
  const candidate = nowEt.clone().isoWeekday(isoDay).startOf("day");
  if (candidate.isBefore(nowEt, "day")) {
    candidate.add(7, "days");
  } else if (candidate.isSame(nowEt, "day")) {
    const [hours, mins] = startTime.split(":").map(Number);
    if (nowEt.hours() * 60 + nowEt.minutes() >= hours * 60 + mins) {
      candidate.add(7, "days");
    }
  }
  return candidate.format("YYYY-MM-DD");
}

// DTSTART anchors on the earliest weekday the event airs; the weekly RRULE
// generates every other occurrence.
export function eventMoments(event) {
  const date = nextOccurrenceET(Math.min(...event.days), event.startTime);
  const start = moment.tz(
    `${date} ${event.startTime}`,
    "YYYY-MM-DD HH:mm",
    EASTERN_TZ,
  );
  const end = start.clone().add(event.durationMinutes, "minutes");
  return { start, end };
}

function buildRrule(event) {
  return `RRULE:FREQ=WEEKLY;BYDAY=${event.days.map((day) => BYDAY[day]).join(",")}`;
}

// RFC 5545 TEXT escaping — backslash, semicolon, comma, newline.
function escapeIcsText(value) {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

function buildDescription(event) {
  return event.host
    ? `Hosted by ${event.host}. TradeAlgo livestream.`
    : "TradeAlgo livestream.";
}

export function buildIcsContent(event) {
  const { start, end } = eventMoments(event);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//TradeAlgo//Livestream Calendar//EN",
    "CALSCALE:GREGORIAN",
    ...NEW_YORK_VTIMEZONE,
    "BEGIN:VEVENT",
    // Stable UID: re-importing a downloaded file updates the existing event
    // instead of duplicating it.
    `UID:${event.id}@tradealgo.com`,
    `DTSTAMP:${moment.utc().format("YYYYMMDD[T]HHmmss[Z]")}`,
    `DTSTART;TZID=${EASTERN_TZ}:${start.format(ICS_LOCAL_FORMAT)}`,
    `DTEND;TZID=${EASTERN_TZ}:${end.format(ICS_LOCAL_FORMAT)}`,
    buildRrule(event),
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(buildDescription(event))}`,
  ];
  if (event.route) {
    lines.push(`URL:${window.location.origin}${event.route}`);
  }
  lines.push("END:VEVENT", "END:VCALENDAR");
  // CRLF terminators are required by RFC 5545 — Outlook rejects LF-only files.
  return `${lines.join("\r\n")}\r\n`;
}

export function downloadIcs(event) {
  const blob = new Blob([buildIcsContent(event)], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${event.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildGoogleCalendarUrl(event) {
  const { start, end } = eventMoments(event);
  // "dates" stays naive (no "Z") on purpose: ctz tells Google to read the
  // values as Eastern. A "Z" suffix makes Google ignore ctz and shift times.
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${start.format(ICS_LOCAL_FORMAT)}/${end.format(ICS_LOCAL_FORMAT)}`,
    ctz: EASTERN_TZ,
    recur: buildRrule(event),
    details: buildDescription(event),
  });
  if (event.route) {
    params.set("location", `${window.location.origin}${event.route}`);
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
